import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { ApiService } from 'lib/services/api.service';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import { timestamp } from 'lib/tools';

const scriptName = 'grecaptcha.service';

declare let grecaptcha: any;
// https://developers.google.com/recaptcha/docs/v3

@Injectable()
export class GrecaptchaService {
    consoleErrorMessage: string = 'reCAPTCHA verification failed';
    isBrowser: boolean;
    uiErrorMessage: string = 'Validation error: Please refresh the page and try again.';

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        @Inject('environment') private environment,
        private apiService: ApiService
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    async getToken(action: string): Promise<string> {
        try {
            if (!this.isBrowser) {
                return;
            }
            if (typeof grecaptcha === 'undefined' || !grecaptcha || typeof grecaptcha.ready !== 'function') {
                console.error(...new ExxComError(698822, scriptName, 'grecaptcha not loaded').stamp());
                return;
            }
            const subdomain = window.location.hostname.split('.')[0];
            action = `${this.environment.siteAbbr}_${subdomain}_${action}`;
            return await new Promise((resolve, reject) => {
                try {
                    grecaptcha.ready(() => {
                        try {
                            grecaptcha.execute(this.environment.google.grecaptchaSiteKey, { action: action }).then(resolve).catch(reject);
                        } catch (executeErr) {
                            console.error(...new ExxComError(698822, scriptName, executeErr).stamp());
                            resolve(null);
                        }
                    });
                } catch (readyErr) {
                    console.error(...new ExxComError(698822, scriptName, readyErr).stamp());
                    resolve(null);
                }
            });
        } catch (err) {
            console.error(...new ExxComError(698822, scriptName, err).stamp());
        }
    }

    async isValid(action: string) {
        try {
            const token = await this.getToken(action);
            const res = await this.verify(token);
            return res.isValid;
        } catch (err) {
            console.error(...new ExxComError(304998, scriptName, err).stamp());
        }
    }

    private async verify(token: string) {
        try {
            const res = await this.apiService.get(`grecaptcha/verify/${token}/${this.environment.siteAbbr}`);
            if (!res.success) {
                console.error(timestamp(), scriptName, 293495, res);
            } else {
                return res.data;
            }
        } catch (err) {
            console.error(...new ExxComError(823994, scriptName, err).stamp());
        }
    }
}
